import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Header as PageHeader } from 'components/Header';
import { Skeleton } from 'components/_shared/Skeleton';
import { Button } from 'components/_shared/Button';
import {
  useGetFieldsQuery,
  useGetFieldQuery,
  useGetSurveyQuery,
  useFinaliseSurveyMutation,
  selectUserTimezone
} from 'state';
import { getAccountToday } from 'utils';
import {
  FieldRail,
  FieldHeader,
  FieldMap,
  SurveyStrip,
  PassList,
  RecordBar,
  FrozenPanel,
  StoneDetail,
  PickList,
  DownloadPanel,
  StoneList,
  DraftBar,
  FinalisePrompt,
  FinaliseLink
} from './components';
import { PICK_LIST_DEFAULT_CAP, COLOR_BY } from './fields.constants';
import {
  SORT_NAME,
  sortFields,
  openSurveyOf,
  effectiveMustPickCm,
  finaliseErrorMessage
} from './fields.utils';
import {
  PageContainer,
  Layout,
  Center,
  Side,
  RetryRow,
  PageMessage
} from './Fields.styles';

const PICK_LIST_RULE = { cap: PICK_LIST_DEFAULT_CAP };

const NO_FIELDS_MESSAGE = 'You have no fields yet.';

const Retry = ({ onRetry }) => (
  <RetryRow>
    <span>Failed to load.</span>
    <Button onClick={onRetry}>Retry</Button>
  </RetryRow>
);

export const Fields = () => {
  const [sortKey, setSortKey] = useState(SORT_NAME);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [selectedStoneId, setSelectedStoneId] = useState(null);
  const [rule, setRule] = useState(PICK_LIST_RULE);
  const [colorBy, setColorBy] = useState(COLOR_BY.RISK);
  const [isPreview, setIsPreview] = useState(false);
  const [draft, setDraft] = useState(null);
  const [finaliseStep, setFinaliseStep] = useState(null);

  const timezone = useSelector(selectUserTimezone);
  const today = timezone ? getAccountToday(timezone) : null;

  const { data, isLoading, isError, refetch } = useGetFieldsQuery();
  const fields = useMemo(() => sortFields(data?.fields ?? [], sortKey), [data, sortKey]);

  useEffect(() => {
    if (fields.some(field => field.id === selectedId)) return;
    setSelectedId(fields[0]?.id ?? null);
  }, [fields, selectedId]);

  const {
    currentData: detail,
    isFetching: isDetailLoading,
    isError: isDetailError,
    refetch: refetchDetail
  } = useGetFieldQuery(selectedId, { skip: !selectedId });

  useEffect(() => {
    setSelectedSurveyId(null);
  }, [selectedId]);

  useEffect(() => {
    setSelectedStoneId(null);
  }, [selectedId, selectedSurveyId]);

  const surveys = detail?.surveys ?? [];
  const openSurvey = openSurveyOf(detail);
  const selectedSurvey =
    surveys.find(survey => survey.id === selectedSurveyId) ??
    openSurvey ??
    surveys[surveys.length - 1];
  const isRecord = Boolean(selectedSurvey && !selectedSurvey.is_open);

  useEffect(() => {
    setIsPreview(false);
    setFinaliseStep(null);
  }, [selectedSurvey?.id]);

  const [finalise, finaliseState] = useFinaliseSurveyMutation();
  const dismissFinalise = () => {
    finaliseState.reset();
    setFinaliseStep(null);
  };
  const openFinaliseConfirm = () => {
    finaliseState.reset();
    setFinaliseStep('confirm');
  };
  const submitFinalise = async () => {
    const result = await finalise({ fieldId: selectedId, surveyId: selectedSurvey.id });
    if (result.error) return;
    setFinaliseStep(null);
    setSelectedSurveyId(null);
  };
  const finaliseError = finaliseState.isError
    ? finaliseErrorMessage(finaliseState.error)
    : null;
  const canFinalise = Boolean(selectedSurvey?.is_open && selectedSurvey.stone_count > 0);
  const renderFinalisePrompt = variant => (
    <FinalisePrompt
      variant={variant}
      survey={selectedSurvey}
      precision={detail.coverage_precision}
      fieldName={selectedField.name}
      isSubmitting={finaliseState.isLoading}
      error={finaliseError}
      onConfirm={submitFinalise}
      onDismiss={dismissFinalise}
    />
  );

  const hasDraft = Boolean(draft) && !isRecord && draft.surveyId === selectedSurvey?.id;
  const listStoneIds = useMemo(
    () => (hasDraft ? new Set(draft.stoneIds) : null),
    [hasDraft, draft]
  );
  const isDrafting = isPreview && hasDraft;
  const mapMustPickCm = effectiveMustPickCm({
    survey: selectedSurvey,
    draft: hasDraft ? draft : null,
    isRecord
  });

  const {
    currentData: surveyDetail,
    isFetching: isSurveyLoading,
    isError: isSurveyError,
    refetch: refetchSurvey
  } = useGetSurveyQuery(selectedSurvey?.id, { skip: !selectedSurvey });

  const selectedField = fields.find(field => field.id === selectedId);

  const renderBody = () => {
    if (isLoading) return <Skeleton height='360px' />;
    if (isError) return <Retry onRetry={refetch} />;
    if (fields.length === 0) return <PageMessage>{NO_FIELDS_MESSAGE}</PageMessage>;

    return (
      <Layout>
        <FieldRail
          fields={fields}
          selectedId={selectedId}
          onSelect={setSelectedId}
          sortKey={sortKey}
          onChangeSort={setSortKey}
        />
        <Center>
          {selectedField && (
            <FieldHeader
              field={selectedField}
              detail={detail}
              survey={selectedSurvey}
              surveyDetail={surveyDetail}
              isRecord={isRecord}
              draft={hasDraft ? draft : null}
              isDetailLoading={isDetailLoading}
            />
          )}
          {isDetailError && <Retry onRetry={refetchDetail} />}
          {isDetailLoading ? (
            <Skeleton height='520px' />
          ) : (
            detail && (
              <FieldMap
                boundaryWkt={detail.field.boundary_wkt}
                surveyId={selectedSurvey?.id}
                isRecord={isRecord}
                isPreview={isDrafting}
                listStoneIds={listStoneIds}
                mustPickCm={mapMustPickCm}
                colorBy={colorBy}
                onChangeColorBy={setColorBy}
                selectedStoneId={selectedStoneId}
                onSelectStone={setSelectedStoneId}
                banner={
                  isRecord ? (
                    <RecordBar
                      survey={selectedSurvey}
                      onBack={() => setSelectedSurveyId(null)}
                    />
                  ) : (
                    isDrafting && (
                      <DraftBar
                        selected={draft.selected}
                        capPerAcre={draft.capPerAcre}
                        capMax={draft.capMax}
                        onBack={() => setIsPreview(false)}
                      />
                    )
                  )
                }
              />
            )
          )}
          {selectedSurvey && (
            <div>
              <SurveyStrip
                surveys={surveys}
                selectedId={selectedSurvey.id}
                onSelect={setSelectedSurveyId}
                precision={detail.coverage_precision}
                today={today}
                timezone={timezone}
              >
                {finaliseStep === 'confirm'
                  ? renderFinalisePrompt('confirm')
                  : canFinalise && <FinaliseLink onClick={openFinaliseConfirm} />}
              </SurveyStrip>
              <PassList
                survey={selectedSurvey}
                passes={surveyDetail?.survey.passes}
                timezone={timezone}
                isLoading={isSurveyLoading}
                isError={isSurveyError}
                onRetry={refetchSurvey}
              />
            </div>
          )}
        </Center>
        <Side>
          {isRecord ? (
            <FrozenPanel
              survey={selectedSurvey}
              surveyDetail={surveyDetail}
              precision={detail.coverage_precision}
              today={today}
              timezone={timezone}
            />
          ) : (
            surveyDetail && (
              <>
                <PickList
                  key={`pick-list-${selectedSurvey.id}`}
                  fieldId={selectedId}
                  surveyId={selectedSurvey.id}
                  mustPickCm={selectedSurvey.must_pick_cm}
                  stoneCount={selectedSurvey.stone_count}
                  acres={surveyDetail.survey.area_acres}
                  rule={rule}
                  onChangeRule={setRule}
                  onListChange={setDraft}
                />
                <DownloadPanel
                  key={selectedSurvey.id}
                  fieldName={selectedField.name}
                  surveyId={selectedSurvey.id}
                  surveyTotal={selectedSurvey.stone_count}
                  draft={hasDraft ? draft : null}
                  isPreview={isPreview}
                  onTogglePreview={() => setIsPreview(value => !value)}
                  onDownloaded={() => {
                    finaliseState.reset();
                    setFinaliseStep('afterDownload');
                  }}
                >
                  {finaliseStep === 'afterDownload' &&
                    renderFinalisePrompt('afterDownload')}
                </DownloadPanel>
                <StoneList
                  surveyId={selectedSurvey.id}
                  stoneCount={selectedSurvey.stone_count}
                  draft={hasDraft ? draft : null}
                  colorBy={colorBy}
                  selectedId={selectedStoneId}
                  onSelect={setSelectedStoneId}
                />
              </>
            )
          )}
          {selectedStoneId && (
            <StoneDetail stoneId={selectedStoneId} mustPickCm={mapMustPickCm} />
          )}
        </Side>
      </Layout>
    );
  };

  return (
    <div>
      <PageHeader />
      <PageContainer>{renderBody()}</PageContainer>
    </div>
  );
};
